import { gql } from '@apollo/client';

// --- Queries ---

// Current staff admin identity. Returns { email, role, disabled }.
// Used by AdminRoleRoute for client-side gating; server still re-checks
// AdminRoleGuard on every staff* call (5s cache).
export const STAFF_ME = gql`
  query staffMe {
    staffMe {
      email
      roles
      disabled
      ownAccountId
      canDecideDunningReview
      intlRequestsVisible
    }
  }
`;

// Staff Customers page — three independent search columns. Each rides an
// existing prod index (no new indexes added):
//   - Accounts → users.emails.address_1 (prefix or @domain) → dedupe by accountId
//   - Users    → users.emails.address_1 (prefix or @domain) → one row per user
//   - Devices  → Atlas Search `deviceSearch` (name/deviceName) + UUID_1 exact match
export const STAFF_SEARCH_ACCOUNTS = gql`
  query staffSearchAccounts($query: String!, $limit: Int = 25) {
    staffSearchAccounts(query: $query, limit: $limit) {
      accountId
      companyName
      ownerEmail
      ownerName
    }
  }
`;

export const STAFF_SEARCH_USERS = gql`
  query staffSearchUsers($query: String!, $limit: Int = 25) {
    staffSearchUsers(query: $query, limit: $limit) {
      userId
      email
      accountId
      companyName
    }
  }
`;

export const STAFF_SEARCH_DEVICES = gql`
  query staffSearchDevices($query: String!, $limit: Int = 25) {
    staffSearchDevices(query: $query, limit: $limit) {
      deviceId
      deviceName
      UUID
      accountId
      companyName
      ownerEmail
      pairingCode
      teamName
    }
  }
`;

// Company header + rollup device/fleet counts for the Customer drill-in page.
export const STAFF_GET_CUSTOMER = gql`
  query staffGetCustomer($accountId: String!) {
    staffGetCustomer(accountId: $accountId) {
      accountId
      companyName
      email
      plan
      planFeature
      totalDevices
      onlineDevices
      staleDevices
      offlineDevices
      totalFleets
      totalTeams
    }
  }
`;

// Cross-tenant device list for a single customer. Includes vpnIp / vpnRegion /
// vpnPublicKey / vpnCreatedAt joined from mdm-devices by UUID. Customer-side
// Device type never exposes these fields.
export const STAFF_CUSTOMER_DEVICES = gql`
  query staffCustomerDevices(
    $accountId: String!
    $filter: StaffDeviceFilterInput
    $pagination: PaginationInput
  ) {
    staffCustomerDevices(accountId: $accountId, filter: $filter, pagination: $pagination) {
      items {
        _id
        accountId
        teamId
        UUID
        deviceName
        status
        os
        osVersion
        model
        platform
        serialNo
        lastHeartBeat
        activationDate
        manufacturer
        localAppVersion
        tags
        tz
        usedStorage
        totalStorage
        currentType
        currentAssignId
        currentAssignName
        vpnIp
        vpnRegion
        vpnPublicKey
        vpnCreatedAt
        fleetId
        path
        classification {
          platform
          vendor
          hardwareFamily
          managementCapability
          osVersionDisplay
          osVersionMajor
          modelCode
          manufacturerRaw
          platformRaw
        }
        availableCommands
        reportedCapabilities
        # Staff-only @ResolveField; see optisigns-open-api
        # src/app/staff/staff-device-capability.resolver.ts. Customer tokens
        # that hit the staff schema will 403 via AdminRoleGuard before this
        # resolver fires. Phase 7 spec §6.3.
        matrixCapabilities
      }
      total
    }
  }
`;

// Fleet card grid for a single customer (read-only on staff side in Phase 1).
export const STAFF_CUSTOMER_FLEETS = gql`
  query staffCustomerFleets($accountId: String!) {
    staffCustomerFleets(accountId: $accountId) {
      _id
      name
      description
      tags
      isDefault
      deviceCount
      onlineCount
      staleCount
      offlineCount
      disallowedCommands
      createdAt
      updatedAt
    }
  }
`;

// Customer's OptiSigns teams (workspaces) + per-team device rollup. Distinct
// from staffCustomerFleets (mdm-fleets). Staff-only (AdminRoleGuard).
export const STAFF_CUSTOMER_TEAMS = gql`
  query staffCustomerTeams($accountId: String!) {
    staffCustomerTeams(accountId: $accountId) {
      _id
      name
      isDefault
      isUnassigned
      deviceCount
      onlineCount
      staleCount
      offlineCount
      createdAt
    }
  }
`;

// Employee WireGuard peers — mdm-devices rows where subnet != 'devices'.
// Grouped by subnet client-side.
export const STAFF_EMPLOYEE_PEERS = gql`
  query staffEmployeePeers($filter: StaffPeerFilterInput) {
    staffEmployeePeers(filter: $filter) {
      _id
      publicKey
      name
      ownerEmail
      vpnIp
      subnet
      region
      createdByRole
      createdAt
      lastHandshakeAt
    }
  }
`;

// Device WireGuard peers — mdm-devices rows where subnet = 'devices'.
// Server-side paginated + searchable (name/vpnIp regex). Fleet can grow into
// 100k devices; client-side pagination is not viable.
export const STAFF_DEVICE_PEERS = gql`
  query staffDevicePeers($filter: StaffDevicePeerFilterInput, $pagination: PaginationInput) {
    staffDevicePeers(filter: $filter, pagination: $pagination) {
      items {
        _id
        publicKey
        name
        vpnIp
        subnet
        region
        createdByRole
        createdAt
        lastHandshakeAt
        deviceName
        accountId
      }
      total
    }
  }
`;

// Reads network.yaml + joins ipam_counters for utilization per subnet.
export const STAFF_SUBNETS = gql`
  query staffSubnets {
    staffSubnets {
      name
      cidr
      type
      allowProvisioner
      used
      total
    }
  }
`;

// Connection-audit log (wg-orchestrator network-truth, PostgreSQL). Root-only on
// the server. Filter optional; offset/limit pagination with a hasMore flag.
export const STAFF_CONNECTION_AUDIT = gql`
  query staffConnectionAudit($filter: ConnectionAuditFilterInput) {
    staffConnectionAudit(filter: $filter) {
      hasMore
      rows {
        ts
        region
        proto
        service
        srcIp
        dstIp
        dstPort
        result
        durationMs
        bytes
        srcSubnet
        dstSubnet
        srcEmail
        srcName
        dstDevice
        dstName
      }
    }
  }
`;

// Full admin_users table — any staff role can read for transparency.
export const STAFF_ADMIN_USERS = gql`
  query staffAdminUsers {
    staffAdminUsers {
      _id
      email
      roles
      invitedBy
      invitedAt
      lastLoginAt
      disabled
      ownAccountId
      createdAt
      updatedAt
    }
  }
`;

// --- Mutations (root-only; resolver re-checks caller.role === 'root') ---

export const STAFF_INVITE_ADMIN_USER = gql`
  mutation staffInviteAdminUser($email: String!, $roles: [AdminRole!]!) {
    staffInviteAdminUser(email: $email, roles: $roles) {
      _id
      email
      roles
      invitedBy
      invitedAt
      lastLoginAt
      disabled
      createdAt
      updatedAt
    }
  }
`;

export const STAFF_UPDATE_ADMIN_USER_ROLE = gql`
  mutation staffUpdateAdminUserRole($email: String!, $roles: [AdminRole!]!) {
    staffUpdateAdminUserRole(email: $email, roles: $roles) {
      _id
      email
      roles
      invitedBy
      invitedAt
      lastLoginAt
      disabled
      createdAt
      updatedAt
    }
  }
`;

export const STAFF_DISABLE_ADMIN_USER = gql`
  mutation staffDisableAdminUser($email: String!) {
    staffDisableAdminUser(email: $email) {
      _id
      email
      roles
      invitedBy
      invitedAt
      lastLoginAt
      disabled
      createdAt
      updatedAt
    }
  }
`;

export const STAFF_REENABLE_ADMIN_USER = gql`
  mutation staffReenableAdminUser($email: String!) {
    staffReenableAdminUser(email: $email) {
      _id
      email
      roles
      invitedBy
      invitedAt
      lastLoginAt
      disabled
      createdAt
      updatedAt
    }
  }
`;

// --- Agent devices (caller's personal device watchlist) ---

// Shared field set for every StaffAgentDevice row. `device` is the full
// StaffDevice projection (same as STAFF_CUSTOMER_DEVICES) so the MDM
// DeviceDetailDrawer renders identically; `found` is false for a removed
// device; current assignment is resolved server-side.
const AGENT_DEVICE_FIELDS = `
  deviceId
  addedAt
  found
  isTestingDevice
  currentType
  currentAssignId
  currentAssignName
  currentAssignOwnerEmail
  device {
    _id
    accountId
    teamId
    UUID
    deviceName
    status
    os
    osVersion
    model
    platform
    serialNo
    lastHeartBeat
    activationDate
    manufacturer
    localAppVersion
    tags
    tz
    usedStorage
    totalStorage
    vpnIp
    vpnRegion
    vpnPublicKey
    vpnCreatedAt
    fleetId
    path
    classification {
      platform
      vendor
      hardwareFamily
      managementCapability
      osVersionDisplay
      osVersionMajor
      modelCode
      manufacturerRaw
      platformRaw
    }
    availableCommands
    reportedCapabilities
    matrixCapabilities
  }
`;

export const STAFF_AGENT_DEVICES = gql`
  query staffAgentDevices {
    staffAgentDevices {
      ${AGENT_DEVICE_FIELDS}
    }
  }
`;

export const STAFF_ADD_AGENT_DEVICE = gql`
  mutation staffAddAgentDevice($deviceId: String!) {
    staffAddAgentDevice(deviceId: $deviceId) {
      ${AGENT_DEVICE_FIELDS}
    }
  }
`;

export const STAFF_REMOVE_AGENT_DEVICE = gql`
  mutation staffRemoveAgentDevice($deviceId: String!) {
    staffRemoveAgentDevice(deviceId: $deviceId) {
      ${AGENT_DEVICE_FIELDS}
    }
  }
`;

// Raw wg-orchestrator peer document for a single device, keyed by UUID.
// Used by the MDM device JSON debug dialog. Staff-only (AdminRoleGuard).
// Non-staff callers receive 403 — the client should detect and hide the tab.
export const STAFF_DEVICE_PEER_RAW = gql`
  query staffDevicePeer($uuid: String!) {
    staffDevicePeer(uuid: $uuid) {
      _id
      publicKey
      name
      ownerEmail
      vpnIp
      subnet
      region
      createdByRole
      createdByTokenId
      createdAt
      updatedAt
    }
  }
`;

// Full raw device document (JSON-stringified) for the MDM device JSON debug
// dialog. Staff-only (AdminRoleGuard). Returns every stored field — the caller
// JSON.parses the string. Used by the "Device" tab of DeviceJsonDialog.
export const STAFF_DEVICE_RAW = gql`
  query staffDeviceRaw($deviceId: String!) {
    staffDeviceRaw(deviceId: $deviceId)
  }
`;

// --- 1-click login ---

export const STAFF_RESOLVE_CUSTOMER = gql`
  query staffResolveCustomer($query: String!) {
    staffResolveCustomer(query: $query) {
      found
      inputEmail
      ownerResolvable
      accountId
      companyName
      plan
      planFeature
      isTrial
      isActive
      ownerEmail
      ownerUserId
      ownerName
      inputIsOwner
      inputRole
      teams {
        teamId
        teamName
        isCurrent
        isTeamBilling
        teamBillingActive
        stripeId
        pairedScreens
        licensedScreens
      }
      domainSearched
      domainMatches {
        accountId
        companyName
        plan
        planFeature
        isTrial
        isActive
        ownerEmail
        ownerName
      }
      otherAccounts {
        accountId
        companyName
        plan
        planFeature
        isTrial
        isActive
        ownerEmail
        ownerName
      }
    }
  }
`;

export const STAFF_GET_CUSTOMER_INFO = gql`
  query staffGetCustomerInfo($accountId: String!) {
    staffGetCustomerInfo(accountId: $accountId) {
      accountId
      payment
      limitation
      feature
      teamPairedScreens
    }
  }
`;

// Rich one-call customer read (identity + plan + trial + entitlements + device/fleet/team
// rollups + the custops_company ops overlay). The staff UI uses it for the
// trialExtensionCount badge; opticlaw-api's `custops` mediator selects the full field set.
export const STAFF_GET_CUSTOMER_360 = gql`
  query staffGetCustomer360($accountId: String!) {
    staffGetCustomer360(accountId: $accountId) {
      accountId
      trialExtensionCount
      lastExtendedAt
    }
  }
`;

export const STAFF_GET_CUSTOMER_INFO_AUDIT_LOG = gql`
  query staffGetCustomerInfoAuditLog($accountId: String!) {
    staffGetCustomerInfoAuditLog(accountId: $accountId) {
      _id
      staffEmail
      changedAt
      changes
      reason
    }
  }
`;

export const STAFF_GET_CUSTOMER_NOTES = gql`
  query staffGetCustomerNotes($accountId: String!) {
    staffGetCustomerNotes(accountId: $accountId) {
      _id
      staffEmail
      createdAt
      text
    }
  }
`;

export const STAFF_ADD_CUSTOMER_NOTE = gql`
  mutation staffAddCustomerNote($accountId: String!, $text: String!) {
    staffAddCustomerNote(accountId: $accountId, text: $text) {
      _id
      staffEmail
      createdAt
      text
    }
  }
`;

export const STAFF_GET_ACTIVE_SESSIONS = gql`
  query staffGetActiveSessions {
    staffGetActiveSessions {
      _id
      staffEmail
      targetAccountId
      targetOwnerEmail
      loginAt
      active
    }
  }
`;

export const STAFF_UPDATE_CUSTOMER_INFO = gql`
  mutation staffUpdateCustomerInfo($accountId: String!, $patch: StaffUpdateCustomerInfoInput!) {
    staffUpdateCustomerInfo(accountId: $accountId, patch: $patch) {
      accountId
      payment
      limitation
      feature
      teamPairedScreens
    }
  }
`;

export const STAFF_LOGIN_AS_CUSTOMER = gql`
  mutation staffLoginAsCustomer($accountId: String!) {
    staffLoginAsCustomer(accountId: $accountId) {
      appUrl
    }
  }
`;

export const STAFF_RESTORE_OWN_ACCOUNT = gql`
  mutation staffRestoreOwnAccount {
    staffRestoreOwnAccount {
      restored
    }
  }
`;

// OptiHub Sales Console — trial/plan/screen tools keyed by Stripe customer ID.
export const STAFF_SALES_EXTEND_TRIAL = gql`
  mutation staffSalesExtendTrial($stripeId: String!, $trialEndDate: String!) {
    staffSalesExtendTrial(stripeId: $stripeId, trialEndDate: $trialEndDate) {
      accountId
      companyName
      message
    }
  }
`;

export const STAFF_SALES_RESET_FREE_TRIAL = gql`
  mutation staffSalesResetToFreeTrial($stripeId: String!, $trialEndDate: String!) {
    staffSalesResetToFreeTrial(stripeId: $stripeId, trialEndDate: $trialEndDate) {
      accountId
      companyName
      message
    }
  }
`;

export const STAFF_SALES_UPLIFT_ENGAGE = gql`
  mutation staffSalesUpliftToEngage($stripeId: String!, $trialEndDate: String!) {
    staffSalesUpliftToEngage(stripeId: $stripeId, trialEndDate: $trialEndDate) {
      accountId
      companyName
      message
    }
  }
`;

export const STAFF_SALES_SET_SCREENS_LIMIT = gql`
  mutation staffSalesSetScreensLimit($stripeId: String!, $maxQuantity: Int!) {
    staffSalesSetScreensLimit(stripeId: $stripeId, maxQuantity: $maxQuantity) {
      accountId
      companyName
      message
    }
  }
`;

export const STAFF_SALES_LOOKUP = gql`
  query staffSalesLookup($stripeId: String!) {
    staffSalesLookup(stripeId: $stripeId) {
      accountId
      companyName
      plan
      planFeature
      isTrial
      trialEndDate
      maxQuantity
    }
  }
`;

// OptiHub Accounts Console.
export const STAFF_ACCOUNTS_CHANGE_COMPANY_OWNER = gql`
  mutation staffAccountsChangeCompanyOwner($currentOwnerEmail: String!, $newOwnerEmail: String!) {
    staffAccountsChangeCompanyOwner(currentOwnerEmail: $currentOwnerEmail, newOwnerEmail: $newOwnerEmail) {
      accountId
      companyName
      message
    }
  }
`;

export const STAFF_ACCOUNTS_CHANGE_TEAM_OWNER = gql`
  mutation staffAccountsChangeTeamOwner($currentOwnerEmail: String!, $newOwnerEmail: String!) {
    staffAccountsChangeTeamOwner(currentOwnerEmail: $currentOwnerEmail, newOwnerEmail: $newOwnerEmail) {
      accountId
      companyName
      message
    }
  }
`;

export const STAFF_ACCOUNTS_RESET_ENGAGE_USAGE = gql`
  mutation staffAccountsResetEngageUsage($stripeId: String!) {
    staffAccountsResetEngageUsage(stripeId: $stripeId) {
      accountId
      companyName
      message
    }
  }
`;

export const STAFF_ACCOUNTS_RESET_CARD_COUNTER = gql`
  mutation staffAccountsResetCardCounter($stripeId: String!) {
    staffAccountsResetCardCounter(stripeId: $stripeId) {
      accountId
      companyName
      message
    }
  }
`;

export const STAFF_ACCOUNTS_NEW_HIRE = gql`
  mutation staffAccountsNewHireEnableAll($stripeId: String!, $trialEndDate: String!) {
    staffAccountsNewHireEnableAll(stripeId: $stripeId, trialEndDate: $trialEndDate) {
      accountId
      companyName
      message
    }
  }
`;

export const STAFF_ACCOUNTS_FORCE_REFRESH = gql`
  mutation staffAccountsForceRefresh($stripeId: String!) {
    staffAccountsForceRefresh(stripeId: $stripeId) {
      accountId
      companyName
      message
    }
  }
`;

// Migrate Branding to scio3 — read-only preflight. Pure read, so it is also the
// post-migration progress check (cfProvisionState + cfSslStatus both 'active').
export const STAFF_BRANDING_MIGRATION_PREFLIGHT = gql`
  query staffBrandingMigrationPreflight($stripeId: String!) {
    staffBrandingMigrationPreflight(stripeId: $stripeId) {
      accountId
      companyName
      host
      subHost
      version
      cfProvisionState
      cfSslStatus
      planFeature
      domainType
      canMigrate
      willGrantAddOn
      checks {
        key
        label
        ok
        blocking
        actual
        detail
      }
    }
  }
`;

export const STAFF_MIGRATE_BRANDING_TO_SCIO3 = gql`
  mutation staffMigrateBrandingToScio3($stripeId: String!) {
    staffMigrateBrandingToScio3(stripeId: $stripeId) {
      accountId
      companyName
      message
    }
  }
`;

// OptiHub Billing Console.
export const STAFF_BILLING_UPDATE_COMPANY_SCREENS = gql`
  mutation staffBillingUpdateCompanyScreens(
    $stripeId: String!
    $totalQuantity: Int!
    $previousQuantity: Int
  ) {
    staffBillingUpdateCompanyScreens(
      stripeId: $stripeId
      totalQuantity: $totalQuantity
      previousQuantity: $previousQuantity
    ) {
      accountId
      companyName
      message
    }
  }
`;

export const STAFF_BILLING_NORMAL_ONBOARDING = gql`
  mutation staffBillingNormalOnboarding(
    $stripeId: String!
    $subscriptionId: String!
    $subQty: Int!
    $subscriptionNormalId: String!
    $invoiceId: String!
    $planFeature: String!
    $currency: String!
    $addOns: [StaffBillingAddOnInput!]
  ) {
    staffBillingNormalOnboarding(
      stripeId: $stripeId
      subscriptionId: $subscriptionId
      subQty: $subQty
      subscriptionNormalId: $subscriptionNormalId
      invoiceId: $invoiceId
      planFeature: $planFeature
      currency: $currency
      addOns: $addOns
    ) {
      accountId
      companyName
      message
    }
  }
`;

export const STAFF_BILLING_USERFUL_ONBOARDING = gql`
  mutation staffBillingUserfulOnboarding(
    $stripeId: String!
    $subscriptionId: String!
    $subQty: Int!
    $subscriptionNormalId: String!
    $planFeature: String!
    $email: String!
    $poNumber: String!
    $contractPeriod: String!
    $purchaseAmount: String!
    $licenseEndDate: String!
    $invoiceId: String!
    $creditNoteId: String!
  ) {
    staffBillingUserfulOnboarding(
      stripeId: $stripeId
      subscriptionId: $subscriptionId
      subQty: $subQty
      subscriptionNormalId: $subscriptionNormalId
      planFeature: $planFeature
      email: $email
      poNumber: $poNumber
      contractPeriod: $contractPeriod
      purchaseAmount: $purchaseAmount
      licenseEndDate: $licenseEndDate
      invoiceId: $invoiceId
      creditNoteId: $creditNoteId
    ) {
      accountId
      companyName
      message
    }
  }
`;

export const STAFF_BILLING_UPDATE_TEAM_SCREENS = gql`
  mutation staffBillingUpdateTeamScreens(
    $stripeId: String!
    $totalQuantity: Int!
    $previousQuantity: Int
  ) {
    staffBillingUpdateTeamScreens(
      stripeId: $stripeId
      totalQuantity: $totalQuantity
      previousQuantity: $previousQuantity
    ) {
      accountId
      companyName
      message
    }
  }
`;

// Billing Console — Set Invoice Status. `invoiceRef` accepts a Stripe invoice id
// (in_...) or an invoice number; `action` is 'paid' | 'void'; `comment` is
// required and gets written onto the invoice in Stripe.
export const STAFF_BILLING_SET_INVOICE_STATUS = gql`
  mutation staffBillingSetInvoiceStatus(
    $invoiceRef: String!
    $action: String!
    $comment: String!
  ) {
    staffBillingSetInvoiceStatus(
      invoiceRef: $invoiceRef
      action: $action
      comment: $comment
    ) {
      invoiceId
      number
      previousStatus
      status
      total
      currency
      billingReason
      customerId
      message
      changed
    }
  }
`;

// Billing Console — Create Hardware Invoice: the catalog behind the pickers.
// Products come from Mongo (`deviceProducts`), so the SKU list follows the catalog
// with no deploy. `shippingMethods` carries the STANDARD method only — staff
// invoices never ship Express — and the card renders it as a label, not a picker.
// `amount` is MINOR units. `invoiceable` is false for a SKU with no Stripe product.
export const STAFF_BILLING_HARDWARE_CATALOG = gql`
  query staffBillingHardwareCatalog {
    staffBillingHardwareCatalog {
      products {
        sku
        stripePriceId
        name
        invoiceable
        usOnly
        isKiosk
        prices {
          currency
          amount
        }
      }
      shippingMethods {
        id
        name
        regions
      }
      currencies
    }
  }
`;

// Billing Console — Create Hardware Invoice. Creates a DRAFT Stripe invoice.
// NOTE: the input carries NO amounts — every price is re-read server-side from
// the device catalog, so the client cannot influence what is billed.
export const STAFF_BILLING_CREATE_HARDWARE_INVOICE = gql`
  mutation staffBillingCreateHardwareInvoice(
    $input: StaffCreateHardwareInvoiceInput!
  ) {
    staffBillingCreateHardwareInvoice(input: $input) {
      invoiceId
      number
      status
      currency
      subtotal
      discountTotal
      shippingTotal
      tax
      total
      lineCount
      dashboardUrl
      automaticTax
      companyName
      accountId
      finalizeScheduled
      finalizesAt
      finalizeRule
      message
    }
  }
`;

// Billing Console — Create Stripe Subscription: the plan catalog behind the pickers.
// Plans and add-on prices come from Mongo's `productPlans` — the SAME collection the
// customer-facing portal prices from — so a staff-created subscription cannot be
// priced differently from a self-serve one, and a catalog change needs no deploy.
//
// `amountMinor` is MINOR units and NULLABLE: an add-on priced in USD that bills other
// currencies via a Stripe multi-currency price has its real amount inside Stripe, so
// under EUR there is no figure to show. `autoConverted` marks exactly that case — the
// card must render it as "billed at Stripe's rate", never as the USD number.
export const STAFF_BILLING_PLAN_CATALOG = gql`
  query staffBillingPlanCatalog {
    staffBillingPlanCatalog {
      plans {
        alias
        name
        prices {
          currency
          amountMinor
          autoConverted
        }
      }
      addOns {
        key
        label
        quantified
        tiers
        requiresPlan
        plans
        prices {
          currency
          amountMinor
          autoConverted
        }
      }
      currencies
      interval
    }
  }
`;

// Billing Console — a Stripe customer's CURRENT billing address, for prefilling the
// Create Stripe Subscription card. Reading before writing is the safety property: the
// operator sees what Stripe holds instead of replacing an address they never saw.
//
// `hasAddress` is false when what Stripe holds is not complete enough for tax, which is
// what makes the card's address fields required rather than optional — with
// automatic_tax on, Stripe REFUSES a subscription it cannot locate.
export const STAFF_BILLING_STRIPE_CUSTOMER = gql`
  query staffBillingStripeCustomer($stripeId: String!) {
    staffBillingStripeCustomer(stripeId: $stripeId) {
      stripeId
      companyName
      ownerEmail
      billingEmail
      accountId
      country
      line1
      line2
      city
      state
      postalCode
      hasAddress
      dunningExclusion
      dunningExclusionUntil
    }
  }
`;

// Billing Console — Dunning control. Writes ONLY Stripe customer metadata
// `staff-app-dunning-exclusion` = 'true' (STOP) | 'pause' (PAUSE, + `untilDate` YYYY-MM-DD,
// written to `staff-app-dunning-exclusion-until-date`) | 'false' (RESUME) — send the enum NAMES.
// Another tool reads the key; staff-app's own dunning ladder does not. `dunningExclusion` in
// the result is Stripe's read-back.
export const STAFF_BILLING_SET_DUNNING_EXCLUSION = gql`
  mutation staffBillingSetDunningExclusion(
    $stripeId: String!
    $value: DunningExclusionValue!
    $comment: String!
    $untilDate: String
  ) {
    staffBillingSetDunningExclusion(
      stripeId: $stripeId
      value: $value
      comment: $comment
      untilDate: $untilDate
    ) {
      stripeId
      accountId
      companyName
      dunningExclusion
      dunningExclusionUntil
    }
  }
`;

// Billing Console — Create Stripe Subscription. Creates the subscription in Stripe
// AND writes the company doc (unlike the two invoice tools, which are Stripe-only).
//
// The input carries no price ids, no amounts, and none of the four Stripe ids Normal
// Onboarding asks for — this mutation CREATES those. `planItemId` in the response is
// the screens line's `si_…`, persisted as `payment.subscription_normal_id`.
//
// Read `mongoUpdated` on the way out: false means the subscription EXISTS in Stripe
// but the account was not updated, so the operator must NOT retry.
// `input.billingAddress` is OPTIONAL and is omitted when the operator did not change
// the prefilled values — an unchanged address costs no Stripe write. When present it is
// written to the customer BEFORE the subscription, because automatic_tax reads the
// customer's location at creation time.
/**
 * The baseline for the "PO / Invoice - Change Screens / Add-ons" card, resolved from the CUSTOMER id.
 *
 * `blockedReason` is why this is a query and not just part of the mutation: an account this
 * tool cannot serve is explained before the operator fills anything in. It carries the exact
 * string "No activated Subscription" for that case.
 */
export const STAFF_BILLING_SUBSCRIPTION_SNAPSHOT = gql`
  query staffBillingSubscriptionSnapshot($stripeId: String!) {
    staffBillingSubscriptionSnapshot(stripeId: $stripeId) {
      accountId
      companyName
      ownerEmail
      subscriptionId
      status
      currency
      planAlias
      planItemId
      stripeQuantity
      mongoQuantity
      quantityDrift
      collectionLabel
      collectionFlipRequired
      addOns {
        key
        label
        tier
        quantity
        subscriptionItemId
      }
      blockedReason
    }
  }
`;

/**
 * Money Issues — PREVIEW setting an account's screen limit, plan and/or MDM / Engage feature flags to what Stripe
 * bills. Name the account by `stripeId` or, for one with no Stripe customer, `accountId` — exactly one.
 * A read (not audited): `outcome` is `in_sync` · `would_apply` · `held`, and `changes` is exactly
 * what the write below would make.
 */
export const STAFF_BILLING_RECONCILE_PREVIEW = gql`
  query staffBillingReconcilePreview($stripeId: String, $accountId: String, $fields: [ReconcileField!]!) {
    staffBillingReconcilePreview(stripeId: $stripeId, accountId: $accountId, fields: $fields) {
      accountId
      companyName
      subscriptionId
      outcome
      heldReason
      changes {
        field
        from
        to
      }
      stripeNormalQty
      stripeFreeDeviceQty
      stripePriceId
      stripePlanAlias
      stripeInterval
      usedSeats
      message
    }
  }
`;

/**
 * Money Issues — set an account's screen limit, plan and/or MDM / Engage feature flags to what Stripe bills
 * (Mongo-only).
 * `outcome` is `applied` · `in_sync` · `held` (with a stable `heldReason` token). `note` says where
 * the correction came from; it lands on the account's Edit History entry and the Activity Log.
 */
export const STAFF_BILLING_RECONCILE_FROM_STRIPE = gql`
  mutation staffBillingReconcileFromStripe(
    $stripeId: String
    $accountId: String
    $fields: [ReconcileField!]!
    $note: String
  ) {
    staffBillingReconcileFromStripe(stripeId: $stripeId, accountId: $accountId, fields: $fields, note: $note) {
      accountId
      companyName
      subscriptionId
      outcome
      heldReason
      changes {
        field
        from
        to
      }
      stripeNormalQty
      stripeFreeDeviceQty
      stripePriceId
      stripePlanAlias
      stripeInterval
      usedSeats
      message
    }
  }
`;

/**
 * Change an existing subscription's screen count and add-ons.
 *
 * Note the input carries NO subscription id and NO prices — the server resolves the
 * subscription from the customer and re-reads every price from the plan catalog.
 */
export const STAFF_BILLING_CHANGE_SUBSCRIPTION = gql`
  mutation staffBillingChangeSubscription($input: StaffChangeSubscriptionInput!) {
    staffBillingChangeSubscription(input: $input) {
      subscriptionId
      previousScreens
      newScreens
      lines {
        key
        label
        subscriptionItemId
        quantity
      }
      status
      currency
      prorationApplied
      prorationInvoiceId
      prorationInvoiceUrl
      prorationSubtotal
      prorationTax
      prorationTotal
      invoiceMemoSet
      invoiceFinalizeScheduled
      invoiceFinalizesAt
      invoiceFinalizeRule
      subscriptionUrl
      deviceItemsCreated
      deviceAmount
      deviceInvoiceId
      deviceInvoiceUrl
      mongoUpdated
      driftHealed
      collectionFlipped
      collectionRestored
      companyName
      accountId
      message
    }
  }
`;

export const STAFF_BILLING_CREATE_STRIPE_SUBSCRIPTION = gql`
  mutation staffBillingCreateStripeSubscription(
    $input: StaffCreateStripeSubscriptionInput!
  ) {
    staffBillingCreateStripeSubscription(input: $input) {
      subscriptionId
      planItemId
      lines {
        key
        label
        subscriptionItemId
        quantity
      }
      status
      currency
      invoiceId
      invoiceNumber
      invoiceStatus
      invoiceSubtotal
      invoiceTax
      invoiceTotal
      invoiceUrl
      invoiceMemoSet
      invoiceFinalizeScheduled
      invoiceFinalizesAt
      invoiceFinalizeRule
      subscriptionUrl
      automaticTax
      mongoUpdated
      companyName
      accountId
      message
    }
  }
`;

// Billing Console — Apply Coupon: the catalog behind the picker. ROOT + SUPPORT.
// Coupons come from staff-app's own `billing_stripe_coupons` table, filtered to the
// active rows server-side — there is no `includeInactive` argument, because the only
// consumer of an inactive row is the ROOT maintenance table below.
//
// `couponsUnavailable` is the field to branch on before saying "no coupons": true means
// the catalog TABLE is missing in this environment (migration 011 never applied — the
// prod migrate job is decoupled and allow_failure), which is a different message and a
// different fix from an environment where nobody has added a row yet.
//
// `discountSummary` is a display cache of what Stripe returned when the id was last
// round-tripped, so a row saved before that existed has none and the card falls back to
// `terms`. It is never the money this tool acts on — Stripe holds the amounts.
export const STAFF_BILLING_COUPON_CATALOG = gql`
  query staffBillingCouponCatalog {
    staffBillingCouponCatalog {
      coupons {
        couponKey
        label
        terms
        requiresTaxExemptDoc
        grantDocumentTag
        discountSummary
        stripeCouponId
      }
      couponsUnavailable
    }
  }
`;

// Billing Console — Apply Coupon: everything the card's customer panel renders, in ONE
// read keyed by the `cus_…` the operator pasted. ROOT + SUPPORT.
//
// One query rather than three because every part of it is a precondition the operator
// has to SEE before submitting: `companyName` proves the id is the intended tenant (it
// is echoed from Mongo, never typed), `currentDiscount` decides whether apply is offered
// at all — Stripe holds exactly one customer-level discount and keeps no history of what
// it displaced, so a non-null one refuses the apply and offers Remove instead — and
// `taxExemptDocuments` is the list the server's document gate will be checked against.
//
// `accountId` is the account the SERVER resolved from `payment.stripe_id`; it is the only
// account the gate ever compares a certificate against. Unverified documents come back
// too, rendered greyed rather than hidden, because "get it verified in the Document
// Center" is the operator's real next step — so select `verifiedAt`, which is what
// decides selectability, not `status` alone. `documentsAvailable: false` means the D1
// binding is unconfigured here, i.e. "cannot look", not "this account has none".
export const STAFF_BILLING_COUPON_PREVIEW = gql`
  query staffBillingCouponPreview($stripeId: String!) {
    staffBillingCouponPreview(stripeId: $stripeId) {
      stripeId
      accountId
      companyName
      stripeName
      currentDiscount {
        id
        couponId
        couponName
        summary
        percentOff
        amountOffMinor
        currency
        duration
        durationInMonths
        startedAt
        endsAt
      }
      taxStatus
      taxExemptDocuments {
        id
        title
        docType
        status
        expirationDate
        verifiedAt
        jurisdiction
      }
      documentsAvailable
    }
  }
`;

// Billing Console — Apply Coupon. Attaches the coupon to the Stripe CUSTOMER, so it
// discounts every future subscription invoice for the coupon's duration. ROOT + SUPPORT.
//
// The input carries `couponKey`, never a Stripe coupon id, no percent and no amount: the
// server re-resolves the id, the terms and the tax-exempt flag from the catalog row on
// every call, so a tampered or stale client can neither attach a coupon nobody approved
// nor clear the document gate by claiming the coupon does not need one. There is also no
// `replaceExisting` — an existing discount is a refusal naming it, not an overwrite.
//
// `discount` is read BACK from Stripe after the write rather than echoed from the
// request: `customers.update` has no compare-and-set, so the read-back is what turns two
// operators racing on the same customer into a reported conflict instead of a false
// success. `documentLinked` is false when the best-effort back-reference onto the
// certificate could not be written — the discount still exists, so the card says so
// rather than implying the link is there. `documentTagApplied` names the Document Center
// tag the apply stamped on that certificate (`non-profit` for NON-PROFIT-10): absent for a
// coupon that stamps none, and absent-with-a-warning-in-`message` when one was due and the
// write did not land. It is server-derived from the coupon KEY, never sent from here. `customerUrl` is built server-side off the
// key's mode, which is why a test-mode result can never deep-link into the live
// dashboard, and it is rendered verbatim.
export const STAFF_BILLING_APPLY_COUPON = gql`
  mutation staffBillingApplyCoupon($input: StaffApplyCouponInput!) {
    staffBillingApplyCoupon(input: $input) {
      stripeId
      accountId
      companyName
      discount {
        id
        couponId
        couponName
        summary
        percentOff
        amountOffMinor
        currency
        duration
        durationInMonths
        startedAt
        endsAt
      }
      previousCouponId
      taxExemptDocumentId
      taxExemptDocumentTitle
      documentLinked
      documentTagApplied
      taxStatus
      taxStatusChanged
      customerUrl
      message
    }
  }
`;

// Billing Console — Apply Coupon: take the customer's discount off. ROOT + SUPPORT.
//
// Flat args, matching the other single-purpose billing mutations. There is deliberately
// NO document gate on the way out: an expired certificate must not block the very action
// its expiry should trigger. The comment is mandatory because a removal is a price
// increase, and the operator's reason is the whole value of the audit row.
//
// Same result type as apply, so one outcome panel renders both. Read it the other way
// round here: `discount` comes back null and `previousCouponId` names what was taken
// away — the only record of it, since Stripe keeps no history of a removed discount.
export const STAFF_BILLING_REMOVE_COUPON = gql`
  mutation staffBillingRemoveCoupon($stripeId: String!, $comment: String!) {
    staffBillingRemoveCoupon(stripeId: $stripeId, comment: $comment) {
      stripeId
      accountId
      companyName
      discount {
        id
        couponId
        couponName
        summary
        percentOff
        amountOffMinor
        currency
        duration
        durationInMonths
        startedAt
        endsAt
      }
      previousCouponId
      taxExemptDocumentId
      taxExemptDocumentTitle
      documentLinked
      documentTagApplied
      taxStatus
      taxStatusChanged
      customerUrl
      message
    }
  }
`;

export const STAFF_MOVE_CONTENT = gql`
  mutation staffMoveContent($contentId: String!, $folderPath: String!) {
    staffMoveContent(contentId: $contentId, folderPath: $folderPath) {
      contentId
      contentType
      oldAccountId
      oldOwnerEmail
      newAccountId
      newOwnerEmail
      newPath
      smallappSynced
      smallappMessage
    }
  }
`;

export const STAFF_POWERBI_VERIFY = gql`
  query staffPowerbiVerify(
    $reportUrl: String!
    $clientId: String!
    $clientSecret: String!
    $tenantId: String!
  ) {
    staffPowerbiVerify(
      reportUrl: $reportUrl
      clientId: $clientId
      clientSecret: $clientSecret
      tenantId: $tenantId
    ) {
      accessToken { ...PowerbiStep }
      embedToken { ...PowerbiStep }
      reportHtml { ...PowerbiStep }
    }
  }
  fragment PowerbiStep on PowerbiVerifyStep {
    outcome
    requestUrl
    httpStatus
    durationMs
    token
    groupId
    reportId
    htmlPreview
    headersJson
    error
    raw
  }
`;

export const STAFF_ACTIVITY_LOG = gql`
  query staffActivityLog($offset: Int, $limit: Int, $search: String, $tool: String) {
    staffActivityLog(offset: $offset, limit: $limit, search: $search, tool: $tool) {
      items {
        id
        time
        tool
        submittedBy
        summary
        status
        detail
      }
      total
      tools
    }
  }
`;

// --- OptiHub Batch Onboarding (Team / Folder Structure Batch Creator) ---
// All three take the customer's own OptiSigns API key; staff-api forwards to
// the public GraphQL gateway server-side (key never persisted/logged).

export const STAFF_BATCH_LIST_TEAMS = gql`
  query staffBatchListTeams($apiKey: String!) {
    staffBatchListTeams(apiKey: $apiKey) {
      id
      name
      description
      usedScreens
      maxScreens
    }
  }
`;

export const STAFF_BATCH_CREATE_TEAMS = gql`
  mutation staffBatchCreateTeams($apiKey: String!, $rows: [StaffTeamRowInput!]!) {
    staffBatchCreateTeams(apiKey: $apiKey, rows: $rows) {
      rowNumber
      name
      ok
      id
      message
    }
  }
`;

export const STAFF_BATCH_CREATE_FOLDERS = gql`
  mutation staffBatchCreateFolders(
    $apiKey: String!
    $teamId: String!
    $rows: [StaffFolderRowInput!]!
  ) {
    staffBatchCreateFolders(apiKey: $apiKey, teamId: $teamId, rows: $rows) {
      rowNumber
      name
      ok
      id
      message
    }
  }
`;

// Administration → Hardware Products (ROOT only). The staff-owned SKU → Stripe
// product map that decides which device-catalog SKUs Create Hardware Invoice can
// bill. MAPPING ONLY — no price: name and price come from Mongo's `deviceProducts`
// at invoice time, so the catalog stays the single source of truth for money.
export const STAFF_BILLING_HARDWARE_PRODUCTS = gql`
  query staffBillingHardwareProducts {
    staffBillingHardwareProducts {
      sku
      stripePriceId
      stripePriceNickname
      stripeProductId
      stripeProductName
      sortOrder
      isActive
      mcfEligible
      shopifyFulfilEligible
      updatedAt
      updatedBy
    }
  }
`;

// The Stripe product id is VALIDATED against Stripe server-side before it is
// stored, and the product's real name comes back for display.
export const STAFF_BILLING_UPSERT_HARDWARE_PRODUCT = gql`
  mutation staffBillingUpsertHardwareProduct(
    $sku: String!
    $stripeProductId: String!
    $stripePriceId: String
    $isActive: Boolean
    # Amazon MCF eligibility. Sent only when the operator changed it — the server treats an
    # absent value as "leave it alone", so repointing a Stripe id cannot withdraw a device from
    # Amazon fulfilment as a side effect.
    $mcfEligible: Boolean
    # All International China / Houston eligibility — same "absent = leave it alone" rule.
    $shopifyFulfilEligible: Boolean
  ) {
    staffBillingUpsertHardwareProduct(
      sku: $sku
      stripeProductId: $stripeProductId
      stripePriceId: $stripePriceId
      isActive: $isActive
      mcfEligible: $mcfEligible
      shopifyFulfilEligible: $shopifyFulfilEligible
    ) {
      sku
      stripePriceId
      stripePriceNickname
      stripeProductId
      stripeProductName
      sortOrder
      isActive
      mcfEligible
      shopifyFulfilEligible
      updatedAt
      updatedBy
    }
  }
`;

// Order of the Create Hardware Invoice dropdown. Sends the WHOLE order (option keys,
// `sku` or `sku|price_…`, in display order) so positions are rewritten from scratch;
// returns the re-ordered rows so the table renders the server's truth, not an optimistic guess.
export const STAFF_BILLING_REORDER_HARDWARE_PRODUCTS = gql`
  mutation staffBillingReorderHardwareProducts($order: [String!]!) {
    staffBillingReorderHardwareProducts(order: $order) {
      sku
      stripePriceId
      stripePriceNickname
      stripeProductId
      stripeProductName
      sortOrder
      isActive
      mcfEligible
      shopifyFulfilEligible
      updatedAt
      updatedBy
    }
  }
`;

// Per-SKU freight BANDS (ROOT only). Money — but freight only: a band cannot change a
// product price, which stays in deviceProducts / Stripe. `amountMinor` is the whole charge
// at that quantity band, in minor units, and 0 legitimately means "ships free from here".
export const STAFF_BILLING_HARDWARE_SHIPPING_TIERS = gql`
  query staffBillingHardwareShippingTiers {
    staffBillingHardwareShippingTiers {
      sku
      region
      currency
      minQuantity
      amountMinor
      updatedAt
      updatedBy
    }
  }
`;

export const STAFF_BILLING_UPSERT_HARDWARE_SHIPPING_TIER = gql`
  mutation staffBillingUpsertHardwareShippingTier(
    $sku: String!
    $minQuantity: Int!
    $amountMinor: Int!
    $region: String
    $currency: String
  ) {
    staffBillingUpsertHardwareShippingTier(
      sku: $sku
      minQuantity: $minQuantity
      amountMinor: $amountMinor
      region: $region
      currency: $currency
    ) {
      sku
      region
      currency
      minQuantity
      amountMinor
      updatedAt
      updatedBy
    }
  }
`;

export const STAFF_BILLING_DELETE_HARDWARE_SHIPPING_TIER = gql`
  mutation staffBillingDeleteHardwareShippingTier(
    $sku: String!
    $minQuantity: Int!
    $region: String
    $currency: String
  ) {
    staffBillingDeleteHardwareShippingTier(
      sku: $sku
      minQuantity: $minQuantity
      region: $region
      currency: $currency
    )
  }
`;

export const STAFF_BILLING_DELETE_HARDWARE_PRODUCT = gql`
  mutation staffBillingDeleteHardwareProduct($sku: String!, $stripePriceId: String) {
    staffBillingDeleteHardwareProduct(sku: $sku, stripePriceId: $stripePriceId)
  }
`;

// Administration → Products List, "Stripe coupons" section (ROOT only). The staff-owned
// catalog of coupons the Billing Console's Apply Coupon card may attach to a customer.
// MAPPING ONLY — there is no percent or amount column anywhere in this table, because
// what a coupon takes off lives in Stripe and a mirror could only drift from the money.
//
// This query returns INACTIVE rows too (the picker's `staffBillingCouponCatalog` does
// not): deactivating is how a coupon is retired without losing the row that explains a
// discount some customer is still carrying.
//
// Rows are per DATABASE, not per environment — sandbox and qa share `optisigns-cdp-devqa`
// while prod has its own, which is what keeps a live-mode coupon id out of the test
// environments and also means an id added in sandbox already exists in qa.
export const STAFF_BILLING_STRIPE_COUPONS = gql`
  query staffBillingStripeCoupons {
    staffBillingStripeCoupons {
      couponKey
      stripeCouponId
      label
      terms
      requiresTaxExemptDoc
      isActive
      stripeCouponName
      stripeValidatedAt
      updatedAt
      updatedBy
    }
  }
`;

// Save is FAIL-CLOSED: the server round-trips the id against this environment's Stripe
// key before storing it and refuses a 404 (with the mode hint), an expired coupon, or a
// 403 — a coupon id has no `prod_`-style prefix, so there is no cheap local typo check
// and an unvalidated row would only fail later, in front of a customer. `stripeCouponName`
// comes back from that round-trip for the success toast.
//
// `requiresTaxExemptDoc` is the switch behind the whole document gate: true means apply
// refuses without a VERIFIED, non-archived `tax_exempt` certificate on the resolved
// account. Both mutations must refetch `staffBillingCouponCatalog` as well as this query,
// or the Billing Console picker keeps serving the pre-edit catalog from cache.
export const STAFF_BILLING_UPSERT_STRIPE_COUPON = gql`
  mutation staffBillingUpsertStripeCoupon(
    $couponKey: String!
    $stripeCouponId: String!
    $label: String!
    $terms: String
    $requiresTaxExemptDoc: Boolean
    $isActive: Boolean
  ) {
    staffBillingUpsertStripeCoupon(
      couponKey: $couponKey
      stripeCouponId: $stripeCouponId
      label: $label
      terms: $terms
      requiresTaxExemptDoc: $requiresTaxExemptDoc
      isActive: $isActive
    ) {
      couponKey
      stripeCouponId
      label
      terms
      requiresTaxExemptDoc
      isActive
      stripeCouponName
      stripeValidatedAt
      updatedAt
      updatedBy
    }
  }
`;

// Deletes the CATALOG row only. Nothing in Stripe is touched and no customer already
// carrying the discount loses it — this only stops the coupon being offered again.
// Prefer Deactivate (a re-upsert with `isActive: false`) when the row still explains a
// live discount.
export const STAFF_BILLING_DELETE_STRIPE_COUPON = gql`
  mutation staffBillingDeleteStripeCoupon($couponKey: String!) {
    staffBillingDeleteStripeCoupon(couponKey: $couponKey)
  }
`;

// ───────────────── OptiHub → Early Promoters Coupon (ROOT only) ─────────────────
//
// The SECOND coupon surface, over a SECOND list (`public.billing_other_coupons`, migration
// 040). Every op below is ROOT-only, the apply included — unlike the approved catalog, where
// ROOT maintains and ROOT+SUPPORT grant. This list removes both of that split's brakes (no
// certificate gate, and an apply here may DISPLACE a discount Stripe keeps no history of), so
// it is confined to the role that owns the list.

// The picker. `couponsUnavailable` separates "migration 040 has not applied here" from "nobody
// has added a row yet" — identical on screen, opposite next actions.
//
// Note the fields this catalog does NOT have next to `staffBillingCouponCatalog`:
// `requiresTaxExemptDoc` and `grantDocumentTag`. There is no column behind either, so this
// list cannot express a certificate requirement the apply path has no code to honour.
export const STAFF_BILLING_OTHER_COUPON_CATALOG = gql`
  query staffBillingOtherCouponCatalog {
    staffBillingOtherCouponCatalog {
      coupons {
        couponKey
        label
        terms
        discountSummary
        stripeCouponId
      }
      couponsUnavailable
    }
  }
`;

// The card's customer panel, in ONE read keyed by the `cus_…` the operator pasted.
//
// LEANER than `staffBillingCouponPreview`, and the difference is the point: no
// `taxExemptDocuments`, no `documentsAvailable`, because this path reads no certificates and
// therefore files no Document Center access-ledger row.
//
// `currentDiscount` non-null does NOT refuse the apply here — it is what makes the card demand
// `replaceExisting`. The three `flag*` fields report what the customer's Stripe metadata holds
// TODAY; they drive a read-only line, never the checkboxes, which are add-only and always start
// unticked.
export const STAFF_BILLING_OTHER_COUPON_PREVIEW = gql`
  query staffBillingOtherCouponPreview($stripeId: String!) {
    staffBillingOtherCouponPreview(stripeId: $stripeId) {
      stripeId
      accountId
      companyName
      stripeName
      currentDiscount {
        id
        couponId
        couponName
        summary
        percentOff
        amountOffMinor
        currency
        duration
        durationInMonths
        startedAt
        endsAt
      }
      taxStatus
      flagEarlyPromotors
      flagReseller
      flagGrantedBy
    }
  }
`;

// The apply.
//
// The input carries a list KEY and never a Stripe coupon id, and the two flags are BOOLEANS,
// never metadata keys or values — the server owns the key spelling (`early_promotors`, which
// really is spelled that way) and only ever writes the literal "true", so this mutation can add
// a segment tag and can never strip one. AT LEAST ONE of them is required; the server refuses
// with a named error, so the card's disabled button is a courtesy and not the rule.
//
// `replaceExisting` is refused-without only when Stripe holds a DIFFERENT coupon. When it
// displaces one, `previousCouponId` names what is gone — with the audit row, the only record
// that it existed. `flagsApplied` reports the metadata keys that LANDED.
export const STAFF_BILLING_APPLY_OTHER_COUPON = gql`
  mutation staffBillingApplyOtherCoupon($input: StaffApplyOtherCouponInput!) {
    staffBillingApplyOtherCoupon(input: $input) {
      stripeId
      accountId
      companyName
      discount {
        id
        couponId
        couponName
        summary
        percentOff
        amountOffMinor
        currency
        duration
        durationInMonths
        startedAt
        endsAt
      }
      previousCouponId
      taxStatus
      taxStatusChanged
      customerUrl
      flagsApplied
      message
    }
  }
`;

// Remove whatever customer-level discount Stripe currently holds, from this tab.
//
// A DIFFERENT op from `staffBillingRemoveCoupon` although both reach the same service method:
// the op name is what the Activity Log files the row under, and a removal done here must not
// appear under the Billing Console's card. ROOT only, unlike its sibling.
//
// Reads the result the other way round from an apply: `discount` comes back null and
// `previousCouponId` names what was taken away — the only record of it, since Stripe keeps no
// history of a removed discount. `taxStatusChanged` is true when the removal ALSO put the
// customer back to Taxable, which happens when the coupon removed was the reason they were
// exempt.
//
// `comment` is OPTIONAL here and `String!` on the Billing Console's equivalent — the rule is
// per-surface, not per-service. One typed anyway is recorded.
export const STAFF_BILLING_REMOVE_OTHER_COUPON = gql`
  mutation staffBillingRemoveOtherCoupon($stripeId: String!, $comment: String) {
    staffBillingRemoveOtherCoupon(stripeId: $stripeId, comment: $comment) {
      stripeId
      accountId
      companyName
      discount {
        id
        couponId
        couponName
        summary
        percentOff
        amountOffMinor
        currency
        duration
        durationInMonths
        startedAt
        endsAt
      }
      previousCouponId
      taxStatus
      taxStatusChanged
      customerUrl
      message
    }
  }
`;

// Administration → Products List, fourth section. Returns INACTIVE rows too (the picker does
// not): deactivating is how a coupon is retired without losing the row that explains a discount
// some customer is still carrying.
export const STAFF_BILLING_OTHER_COUPONS = gql`
  query staffBillingOtherCoupons {
    staffBillingOtherCoupons {
      couponKey
      stripeCouponId
      label
      terms
      isActive
      stripeCouponName
      stripeValidatedAt
      updatedAt
      updatedBy
    }
  }
`;

// FAIL-CLOSED, like its sibling: the server round-trips the id against this environment's
// Stripe key before storing it. It ALSO refuses a coupon already in the Billing Console's
// approved catalog — one coupon belongs to one list, or the same discount becomes grantable
// under two different rule sets with nothing saying which authorised a grant.
//
// Both mutations must refetch `staffBillingOtherCouponCatalog` as well as this query, or the
// Early Promoters Coupon picker keeps serving the pre-edit list from cache.
export const STAFF_BILLING_UPSERT_OTHER_COUPON = gql`
  mutation staffBillingUpsertOtherCoupon(
    $couponKey: String!
    $stripeCouponId: String!
    $label: String!
    $terms: String
    $isActive: Boolean
  ) {
    staffBillingUpsertOtherCoupon(
      couponKey: $couponKey
      stripeCouponId: $stripeCouponId
      label: $label
      terms: $terms
      isActive: $isActive
    ) {
      couponKey
      stripeCouponId
      label
      terms
      isActive
      stripeCouponName
      stripeValidatedAt
      updatedAt
      updatedBy
    }
  }
`;

// Deletes the LIST row only. Nothing in Stripe is touched and no customer already carrying the
// discount loses it — this only stops the coupon being offered again. Prefer Deactivate (a
// re-upsert with `isActive: false`) when the row still explains a live discount.
export const STAFF_BILLING_DELETE_OTHER_COUPON = gql`
  mutation staffBillingDeleteOtherCoupon($couponKey: String!) {
    staffBillingDeleteOtherCoupon(couponKey: $couponKey)
  }
`;

/**
 * Subscription health for the customer badge. Best-effort server-side: a Stripe failure comes
 * back as an empty status rather than an error, so this can never break a customer page.
 */
export const STAFF_BILLING_SUBSCRIPTION_STATUS = gql`
  query staffBillingSubscriptionStatus($stripeId: String!) {
    staffBillingSubscriptionStatus(stripeId: $stripeId) {
      stripeId
      status
      subscriptionId
      multiple
    }
  }
`;

// ─── Dunning manager review ────────────────────────────────────────────────

/**
 * The sweeps currently refusing to run. Read on the review page because an abort is the reason
 * the queue below it may be empty or stale — nothing advances while one is open.
 */
export const DUNNING_SWEEP_ABORTS = gql`
  query dunningSweepAborts {
    dunningSweepAborts {
      job
      candidates
      cap
      detail
      firstSeenAt
      lastSeenAt
      ticks
    }
  }
`;

export const DUNNING_REVIEW_QUEUE = gql`
  query dunningReviewQueue {
    dunningReviewQueue {
      accountId
      companyName
      ladder
      startedAt
      daysInDunning
      screenCount
      amountDue
      invoiceId
      stripeId
      holdReason
      history
      reviewSince
      isActive
      isTrial
      ownerEmail
      billingEmail
      subscriptionStatus
      subscriptionMultiple
      dunningExclusion
      dunningExclusionUntil
    }
  }
`;

export const DUNNING_SUSPENDED_LIST = gql`
  query dunningSuspendedList {
    dunningSuspendedList {
      accountId
      companyName
      ladder
      startedAt
      daysInDunning
      screenCount
      amountDue
      invoiceId
      stripeId
      holdReason
      history
      reviewSince
      isActive
      isTrial
      ownerEmail
      billingEmail
      subscriptionStatus
      subscriptionMultiple
      dunningExclusion
      dunningExclusionUntil
    }
  }
`;

// Dunning Review → "Dunning Exclusion List": customers whose Stripe metadata
// `staff-app-dunning-exclusion` is 'true' or 'pause'. A LIVE Stripe search; `search` matches
// customer email, domain or Stripe id.
export const DUNNING_STRIPE_EXCLUSIONS = gql`
  query dunningStripeExclusions($search: String) {
    dunningStripeExclusions(search: $search) {
      stripeId
      value
      untilDate
      stripeEmail
      stripeName
      accountId
      companyName
      ownerEmail
      isActive
      isTrial
      subscriptionStatus
      subscriptionMultiple
    }
  }
`;

// Reactivate a DEACTIVATED account: screens + portal back, episode back to review (the
// invoice is still unpaid). Same gate as Deactivate.
export const REACTIVATE_DUNNING_ACCOUNT = gql`
  mutation reactivateDunningAccount($accountId: String!, $reason: String!) {
    reactivateDunningAccount(accountId: $accountId, reason: $reason) {
      applied
      state
      screenPush
      message
    }
  }
`;

export const DECIDE_DUNNING_REVIEW = gql`
  mutation decideDunningReview($accountId: String!, $reason: String!) {
    decideDunningReview(accountId: $accountId, reason: $reason) {
      applied
      state
    }
  }
`;

// OptiHub › Account Audit Log — a customer's own activity log, read-only (ROOT + SUPPORT).
export const STAFF_ACCOUNT_AUDIT_RESOLVE = gql`
  query staffAccountAuditResolve($query: String!) {
    staffAccountAuditResolve(query: $query) {
      accountId
      companyName
      ownerEmail
      stripeId
      matchedTeam
    }
  }
`;

export const STAFF_ACCOUNT_AUDIT_LOGS = gql`
  query staffAccountAuditLogs(
    $accountId: String!
    $filter: StaffAccountAuditFilterInput
    $pagination: StaffAccountAuditPaginationInput
  ) {
    staffAccountAuditLogs(accountId: $accountId, filter: $filter, pagination: $pagination) {
      totalCount
      totalCapped
      dateFrom
      dateTo
      items {
        id
        createdAt
        time
        action
        type
        text
        teamId
        teamName
        userId
        userName
        userEmail
        description
        ipAddress
        isSuperUser
        staffEmail
      }
    }
  }
`;
